import React, { useMemo, useRef, useState } from 'react';
import { CreditCard, Loader2, AlertCircle, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { paymentCardGradient } from '@/components/payment/PaymentCardGradientStyles';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';
import { deferaCapitalNmiUrl } from '@/lib/apiUrls';
import { mergeSupabaseEdgeHeaders } from '@/lib/reserveActAs';
import { nmiDeclineMessage } from '@/lib/nmiDeclineMessage';
import { cn } from '@/lib/utils';
import { formatScheduleDate } from '@/utils/formatScheduleDate';
import { buildNmiSkipPaymentPreview, INELIGIBLE_MESSAGES } from '@/utils/nmiSkipPaymentPreview';
import {
  MODAL_GHOST_BTN_CLASS,
  MODAL_PRIMARY_BTN_CLASS,
} from '@/components/booking/paymentScheduleModalStyles';

const fmt = (cents) => (cents / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/**
 * Defera Capital — Skip Payment form for vault-charged installment plans.
 *
 * Preview is built locally from the live schedule rows the parent already
 * fetched — no preview API round-trip required. Confirm charges the skip fee
 * via `defera-capital-nmi/skip-payment`.
 */
export default function DeferaCapitalNmiSkipPaymentForm({
  tripId,
  onClose,
  onSuccess,
  upcomingRows = [],
  openInvoicesTotalCents = 0,
  onPaymentUncertain,
}) {
  const [charging, setCharging] = useState(false);
  const [error, setError] = useState(null);
  const payNonceRef = useRef(null);

  const preview = useMemo(
    () => buildNmiSkipPaymentPreview(upcomingRows, { openInvoicesTotalCents }),
    [upcomingRows, openInvoicesTotalCents],
  );

  const canSkip = preview?.can_skip === true;
  const feeCents = preview?.skip_fee_cents ?? 0;
  const skippedDate = preview?.skipped_date;
  const skippedAmountCents = preview?.skipped_amount_cents ?? 0;

  const handleConfirm = async () => {
    if (!tripId || !canSkip || charging) return;
    if (!payNonceRef.current) {
      payNonceRef.current = (typeof crypto !== 'undefined' && crypto.randomUUID)
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    }

    setCharging(true);
    setError(null);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) throw new Error('Please sign in');
      const res = await fetch(deferaCapitalNmiUrl('skip-payment'), {
        method: 'POST',
        headers: mergeSupabaseEdgeHeaders({
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
        }),
        body: JSON.stringify({
          booking_id: tripId,
          client_request_id: payNonceRef.current,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        const friendly = nmiDeclineMessage(data, {
          defaultMessage: "We couldn't complete the skip fee charge. Please try again, or update your card.",
        });
        if (res.status >= 500) {
          onPaymentUncertain?.();
        }
        console.error('[defera-capital-nmi] skip-payment error', {
          rawText: friendly.dev,
          error_type: data?.error_type,
          gateway_response: data?.gateway_response,
        });
        const err = new Error(friendly.customer);
        err.nmiAction = friendly.action;
        throw err;
      }

      payNonceRef.current = null;
      const nextDate = preview?.new_next_charge_date
        ? formatScheduleDate(preview.new_next_charge_date, 'EEE, MMM d')
        : null;
      toast.success(nextDate ? `Payment skipped — your next charge is ${nextDate}.` : 'Payment skipped.');
      onSuccess?.();
      onClose?.();
    } catch (err) {
      const msg = err?.message || 'Skip payment failed';
      setError(msg);
      toast.error(msg, { description: err?.nmiAction || undefined });
      if (err?.name === 'TypeError' || /fetch|network|gateway/i.test(msg)) {
        onPaymentUncertain?.();
      }
    } finally {
      setCharging(false);
    }
  };

  return (
    <div className="space-y-4">
      {error && (
        <div className="px-3 py-2 rounded-lg bg-red-500/[0.07] border border-red-500/20">
          <p className="text-[12px] text-red-300/90 leading-snug flex items-center gap-2">
            <AlertCircle className="w-3.5 h-3.5 shrink-0" />
            {error}
          </p>
        </div>
      )}

      {!canSkip && !error && (
        <div className="px-3 py-2 rounded-lg bg-brand-500/[0.07] border border-brand-500/20">
          <p className="text-[12px] text-brand-300/90 leading-snug">
            {preview.message || INELIGIBLE_MESSAGES[preview.reason] || "This trip isn't eligible to skip a payment right now."}
          </p>
        </div>
      )}

      {canSkip && (
        <>
          <div className="flex items-center justify-between rounded-xl bg-white/[0.03] border border-white/[0.08] px-3 py-2.5">
            <div>
              <p className="text-white/40 text-[10px] uppercase tracking-wider mb-0.5">Skipping</p>
              <p className="text-white/85 text-sm font-medium">
                {formatScheduleDate(skippedDate)}
              </p>
            </div>
            <div className="flex items-center gap-2 text-sm tabular-nums">
              <span className="text-white/40 line-through">${fmt(skippedAmountCents)}</span>
              <ArrowRight className="w-3 h-3 text-white/25" />
              <span className="text-brand-300 font-medium">$0.00</span>
            </div>
          </div>

          {preview.current_schedule?.dates?.length > 0 && preview.proposed_schedule?.dates?.length > 0 && (
            <div className="grid grid-cols-2 gap-3">
              <div className="rounded-xl border border-white/[0.06] bg-white/[0.015] p-3">
                <p className="text-white/30 text-[10px] uppercase tracking-wider mb-2">Current</p>
                <p className="text-white/50 text-xs font-normal mb-2">
                  {preview.current_schedule.num_payments} × ${fmt(preview.current_schedule.per_payment_cents || 0)}
                </p>
                <div className="space-y-1 max-h-32 overflow-y-auto pr-1">
                  {preview.current_schedule.dates.map((d, i) => (
                    <div key={i} className="flex justify-between text-[11px]">
                      <span className="text-white/25">{formatScheduleDate(d.date, 'MMM d')}</span>
                      <span className="text-white/35">${fmt(d.amount_cents || 0)}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="rounded-xl border border-brand-500/25 bg-brand-500/[0.06] p-3">
                <p className="text-brand-300/80 text-[10px] uppercase tracking-wider mb-2">New</p>
                <p className="text-brand-200/90 text-xs font-normal mb-2">
                  {preview.proposed_schedule.num_payments} × ${fmt(preview.proposed_schedule.per_payment_cents || 0)}
                </p>
                <div className="space-y-1 max-h-32 overflow-y-auto pr-1">
                  {preview.proposed_schedule.dates.map((d, i) => (
                    <div key={i} className="flex justify-between text-[11px]">
                      <span className={d.amount_cents === 0 ? 'text-brand-300/40 line-through' : 'text-brand-300/50'}>
                        {formatScheduleDate(d.date, 'MMM d')}
                      </span>
                      <span className={d.amount_cents === 0 ? 'text-brand-200/60' : 'text-brand-200/70'}>
                        {d.amount_cents === 0 ? '$0.00' : `$${fmt(d.amount_cents)}`}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {preview.current_schedule?.per_payment_cents > 0 && preview.proposed_schedule?.per_payment_cents > 0 && (
            <div className="flex justify-center gap-3 text-xs">
              <span className="text-white/35">
                ${fmt(preview.current_schedule.per_payment_cents)} × {preview.current_schedule.num_payments}
              </span>
              <ArrowRight className="w-3 h-3 text-white/20" />
              <span className="text-brand-300">
                ${fmt(preview.proposed_schedule.per_payment_cents)} × {preview.proposed_schedule.num_payments}
              </span>
            </div>
          )}

          <div className={paymentCardGradient.container}>
            <p className="text-white/70 text-xs font-normal uppercase tracking-wider mb-2">Skip fee today</p>
            <div className="flex items-center justify-between">
              <span className="text-white/60 text-sm">Charge now</span>
              <span className="text-white font-normal text-base tabular-nums">${fmt(feeCents)}</span>
            </div>
            <p className="text-white/60 text-xs mt-2">
              The fee does not go toward your trip balance. Your payment dates stay the same — your remaining installments go up to cover the skipped amount.
            </p>
          </div>

          <div className="flex items-center gap-2 text-white/60 text-xs">
            <CreditCard className="w-3.5 h-3.5" />
            We&apos;ll charge the card you used to book this trip. To change it, use Update Card.
          </div>
        </>
      )}

      <div className="flex gap-2.5 pt-2">
        <Button
          type="button"
          onClick={handleConfirm}
          disabled={charging || !canSkip}
          className={cn(MODAL_PRIMARY_BTN_CLASS, 'flex-1 text-xs sm:text-sm')}
        >
          {charging ? (
            <span className="inline-flex items-center gap-1.5">
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
              Processing…
            </span>
          ) : (
            `Pay $${fmt(feeCents)} & Skip`
          )}
        </Button>
        <Button
          type="button"
          variant="ghost"
          onClick={onClose}
          disabled={charging}
          className={cn(MODAL_GHOST_BTN_CLASS, 'px-5 text-xs sm:text-sm')}
        >
          Cancel
        </Button>
      </div>
    </div>
  );
}
